import { h, Fragment } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import PropTypes from 'prop-types';

import { className, getClassNames } from '../../../../utils/classnames-utils';
import Option from '../../2-atoms/option/option';
import Switch from '../../2-atoms/forms/switch/switch';
import { getLabel } from '../../../../labels/labelling-service';

import { modifierList } from './props-list';

const BODY_BOTTOM_PADDING = 50;
const DEFAULT_OPTION_MODIFIERS = [];

const SelectBox = ({
  title,
  showMore,
  showLess,
  message,
  options,
  enableSelectAll,
  onOptionClick,
  selectedOptions = [],
  modifiers = DEFAULT_OPTION_MODIFIERS,
  onSwitchClick,
}) => {
  const goToSectionLabel = getLabel('you.are.following.link.label');
  const boxRef = useRef();
  const offsetHeight = boxRef.current?.offsetHeight || 0;
  const scrollHeight = boxRef.current?.scrollHeight || 0;
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(null);
  const [isShowMoreVisible, setIsShowMoreVisible] = useState(false);

  const footerClassName = className({
    'c-select-box__footer': true,
    'c-select-box__footer--open': isOpen,
  });

  const arrowClassName = className({
    'c-select-box__footer-arrow': true,
    'c-select-box__footer-arrow--open': isOpen,
  });

  const bodyClassName = className({
    'c-select-box__body': true,
    'c-select-box__body--with-show-more': isShowMoreVisible,
  });

  useEffect(() => {
    setHeight({
      original: offsetHeight,
      full: scrollHeight + BODY_BOTTOM_PADDING,
    });

    if (scrollHeight > offsetHeight) {
      setIsShowMoreVisible(true);
    }
  }, [scrollHeight]);

  useEffect(() => {
    if (height) {
      const maxHeight = isOpen ? height.full : height.original;
      boxRef.current.style.maxHeight = `${maxHeight}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleShowMore = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const hasSingleOption = options.length === 1;

  const classNames = getClassNames('c-select-box', modifiers);

  return (
    <div className={classNames} ref={boxRef}>
      {!hasSingleOption && title && (
        <Fragment>
          <h3 className="c-select-box__header">
            <span>{title}</span>
            <span>
              ({selectedOptions.length}/{options.length})
            </span>
          </h3>
          <hr className="c-select-box__hr" />
        </Fragment>
      )}
      <div className={bodyClassName}>
        {message && (
          <div className="c-select-box__message">
            {message}
            {enableSelectAll && <Switch onClick={onSwitchClick} checked={options.length === selectedOptions.length} />}
          </div>
        )}
        <div className="c-select-box__options">
          {options.map((option) => (
            <Option
              key={option.value}
              modifiers={option.modifiers?.length ? option.modifiers : ['radio']}
              isActive={option.isActive}
              isDisabled={option.isDisabled}
              onClick={() => onOptionClick(option)}
              image={option.image}
              text={option.text}
              urlLink={option.urlLink}
              labelLink={goToSectionLabel}
              target={option.target}
              intcmp={option.intcmp}
            />
          ))}
        </div>
      </div>
      {isShowMoreVisible && !hasSingleOption && (
        <button type="button" className={footerClassName} onClick={handleShowMore}>
          {isOpen ? showLess : showMore} <span className={arrowClassName} />
        </button>
      )}
    </div>
  );
};

SelectBox.propTypes = {
  title: PropTypes.string,
  showMore: PropTypes.string,
  showLess: PropTypes.string,
  message: PropTypes.string,
  options: PropTypes.array.isRequired,
  enableSelectAll: PropTypes.bool,
  onOptionClick: PropTypes.func,
  selectedOptions: PropTypes.array,
  modifiers: PropTypes.arrayOf(PropTypes.oneOf(modifierList)),
  maxOptions: PropTypes.number,
  onSwitchClick: PropTypes.func,
};

export default SelectBox;
